// src/utils/errorUtils.js

/**
 * エラーコード定数
 */
export const ERROR_CODES = {
  SSO_FAILED: 'SSO_FAILED',
  DEFAULT: 'DEFAULT'
};

/**
 * エラーコンテンツ定義
 */
const ERROR_CONTENTS = {
  [ERROR_CODES.SSO_FAILED]: {
    title: 'SSO認証エラー',
    message: 'SSOの認証プロセスで予期しないエラーが発生しました。'
  },
  [ERROR_CODES.DEFAULT]: {
    title: 'エラー',
    message: 'ASFiNDERでエラーが発生しました。'
  }
};

/**
 * エラーID生成（UUID v4形式）
 * @returns {string} UUID形式のエラーID（crypto.randomUUIDが利用可能な場合）、または空文字列
 */
export const generateErrorId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return '';
};

/**
 * エラー詳細をsessionStorageに保存
 * @param {Object} detail - エラー詳細データ
 */
const saveErrorDetailToSession = (detail) => {
  try {
    sessionStorage.setItem('errorDetail', JSON.stringify({ ...detail, timestamp: new Date().toISOString() }));
  } catch (e) {
    console.error('エラー詳細の保存に失敗:', e);
  }
};

/**
 * エラー詳細を取得して削除
 * @returns {Object|null} エラー詳細データ
 */
const getAndClearErrorDetailFromSession = () => {
  try {
    const detailJson = sessionStorage.getItem('errorDetail');
    if (!detailJson) return null;
    
    sessionStorage.removeItem('errorDetail');
    return JSON.parse(detailJson);
  } catch (e) {
    console.error('エラー詳細の取得に失敗:', e);
    return null;
  }
};

/**
 * UUIDの形式を検証
 * @param {string} uuid - 検証するUUID文字列
 * @returns {boolean} UUID形式として有効な場合true
 */
const isValidUUID = (uuid) => {
  if (!uuid) return false;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
};

/**
 * クエリパラメータからエラーデータを取得
 * @param {URLSearchParams} searchParams - URLSearchParamsオブジェクト
 * @returns {Object} エラーデータ
 */
export const getErrorData = (searchParams) => {
  const code = searchParams.get('errorCode');
  const errorId = searchParams.get('errorId');
  
  // エラーコードの検証
  let errorContent;
  if (!code || !ERROR_CONTENTS[code]) {
    if (code) {
      console.error('無効なエラーコード:', code);
    }
    errorContent = ERROR_CONTENTS[ERROR_CODES.DEFAULT];
  } else {
    errorContent = ERROR_CONTENTS[code];
  }
  
  // エラーIDの検証
  let validatedErrorId = null;
  if (errorId) {
    if (isValidUUID(errorId)) {
      validatedErrorId = errorId;
    } else {
      console.error('無効なエラーID:', errorId);
    }
  }
  
  return {
    title: errorContent.title,
    message: errorContent.message,
    errorId: validatedErrorId
  };
};

/**
 * エラー情報をログ出力（Error.jsから呼ばれる）
 * errorDataとerrorDetailの両方をログに出力
 * @param {URLSearchParams} searchParams - URLSearchParamsオブジェクト
 */
export const logError = (searchParams) => {
  const errorData = getErrorData(searchParams);
  const errorDetail = getAndClearErrorDetailFromSession();
  
  console.log('エラー情報:', {
    ...errorData,
    detail: errorDetail
  });
};

/**
 * エラーページへ遷移
 * @param {string} code - エラーコード（必須）
 * @param {Function} navigate - react-routerのnavigate関数（必須）
 * @param {string|null} errorId - エラーID（オプション、UUID形式）
 * @param {Object|null} detail - エラー詳細情報（オプション、ログ出力のみ）
 * @param {Object} options - オプション
 * @param {boolean} options.replace - 履歴を置き換えるか（デフォルト: true）
 */
export const navigateToErrorPage = (code, navigate, errorId=null, detail=null, options = {}) => {
  const { replace = true } = options;
  
  // 詳細情報のみsessionStorageへ
  if (detail) {
    saveErrorDetailToSession(detail);
  }
  
  // クエリパラメータ生成
  const params = new URLSearchParams();

  // エラーコード：指定がなければDEFAULTを設定
  params.set('errorCode', code || ERROR_CODES.DEFAULT);

  // エラーID：UUID形式の場合のみ設定
  if (errorId && isValidUUID(errorId)) {
    params.set('errorId', errorId);
  }
  
  navigate(`/error?${params.toString()}`, { replace });
};
